// Centralized helpers for the canonical, backend-derived billing financial
// model. This module intentionally contains NO financial formulas of its
// own (no `amount - paid`, no status derivation) - every value it reads
// comes straight from the API response, which is the single source of
// truth (see be/multi-clinic-api's computeBillingFinancials.ts). Adding a
// competing calculation here would risk disagreeing with the backend.

export type BillingStatus =
  | "UNPAID"
  | "PARTIAL"
  | "PAID"
  | "WAIVED"
  | "OVERPAID";

const SETTLED_STATUSES: ReadonlySet<string> = new Set([
  "PAID",
  "WAIVED",
  "OVERPAID",
]);

/**
 * Whether a payment can be initiated against this billing right now.
 * A billing is payable only while money is still owed (balance > 0) and its
 * canonical status isn't already a settled one - PAID, WAIVED and OVERPAID
 * all mean nothing further should be collected. Checking status ALONE
 * (the historical `status === "PAID"` check) missed WAIVED and OVERPAID;
 * checking balance ALONE would be enough in theory but the explicit status
 * check makes the "why" visible at the call site and is cheap insurance
 * against a future field rename.
 */
export const canInitiatePayment = (
  status: string | undefined | null,
  balance: number | undefined | null
): boolean => {
  const numericBalance = Number(balance ?? 0);
  if (numericBalance <= 0) return false;
  if (!status) return true;
  return !SETTLED_STATUSES.has(status);
};

/**
 * Human-readable reason a payment can't be initiated, for a toast/info
 * message. Returns null when payment IS allowed.
 */
export const getPaymentBlockedReason = (
  status: string | undefined | null,
  balance: number | undefined | null
): string | null => {
  if (canInitiatePayment(status, balance)) return null;

  switch (status) {
    case "PAID":
      return "This session has already been paid in full.";
    case "WAIVED":
      return "This session's balance was fully waived by discount - no payment is needed.";
    case "OVERPAID":
      return "This session has already been overpaid - no further payment is needed.";
    default:
      return "This session has no remaining balance.";
  }
};
